import {Component, EventEmitter, Input} from '@angular/core';
import {SettingsPageComponent} from "../../../x-components/settings/settings-page/settings-page.component";
import {AppRoutes} from "../../../enums/routes";
import {SettingsInputElementModel} from "../../../models/settings-input-element-model";
import {ActivatedRoute, Router} from "@angular/router";
import {PermissionService} from "../../../_auth/permission.service";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {ModelBase} from "../../../models/model-base";
import {HttpPropertyChangeStatus} from "../../../enums/http-property-change-status";
import {ObservableQueueElement} from "../../../models/observable-queue-element";
import {AlertLevel} from "../../../enums/alert-level";
import {CompanyHttpService} from "../../x-http-requests/company-http.service";
import {CompanyDto} from "../../../models/admin_owner/company/company-dto";
import {
  ApiErrorInfo, CompanyAliasResult
} from "../../../models/admin_owner/company/company-alias-result";
import {
  describeAdministrationCode, describeAdministrationError
} from "../../../_shared/errors/administration-errors";
import {Address} from "../../../models/common/address";
import {
  SettingsInputElementComponent
} from "../../../x-components/settings/settings-input-element/settings-input-element.component";
import {CommonModule} from "@angular/common";

/**
 * The sentence for a DNS warning this build has no translation for.
 *
 * Deliberately says what *did* happen first: the alias is stored and usable in every place an alias is used —
 * only the web address is missing — and a reader who takes „Fehler" at face value would otherwise undo a save
 * that worked. Names the screen that shows the real state, because that is where the remedy lives.
 */
const DNS_WARNING_FALLBACK =
  'Der Alias wurde gespeichert, die zugehörige Web-Adresse konnte aber nicht angelegt werden. '
  + 'Den Stand sehen Sie unter „Integrationen" → „DNS"; CheqMe versucht es automatisch erneut.';


@Component({
  selector: 'app-company-setting',
  templateUrl: '../../../x-components/settings/settings-page/settings-page.component.html',
  imports: [
    CommonModule,
    SettingsInputElementComponent
  ],
  styleUrls: ['./company-setting.component.scss']
})
export class CompanySettingComponent extends SettingsPageComponent {
  public redirectRoute: string = AppRoutes.OwnerCompanies;

  public settingsInputElementModel: { [key: string]: SettingsInputElementModel } = {
    newCompanyName: new SettingsInputElementModel(),
    newCompanyAlias: new SettingsInputElementModel(),

    // address
    address: new SettingsInputElementModel(),

    // tax
    taxSeparator: new SettingsInputElementModel(),
    taxNumber: new SettingsInputElementModel(),
    taxId: new SettingsInputElementModel(),
  };
  public model?: CompanyDto;
  public modelChanged = new EventEmitter<ModelBase>();
  public timeoutClick: boolean = false;

  /**
   * The DNS problem the last alias save reported, held until the queue settles.
   *
   * `SetAlias` answers `200 { company, dnsWarning }` (#637): a DNS failure never blocks the save, so it is not
   * an error — but the operator still has to be told, and only after the success banner the queue posts.
   * Cleared as it is read, so a second save does not report the first one's warning again.
   */
  private dnsWarning: ApiErrorInfo | null = null;

  constructor(route: ActivatedRoute,
              PermissionService: PermissionService,
              public companyHttpService: CompanyHttpService,
              router: Router,
              globalAlertService: GlobalAlertService) {
    super(route, PermissionService, router, globalAlertService);
    this.settingsInputElementModel.newCompanyName.propertyTitle = "Name";
    this.settingsInputElementModel.newCompanyName.requiredPermission = PermissionService.Owner();
    this.settingsInputElementModel.newCompanyName.order = 1;

    this.settingsInputElementModel.newCompanyAlias.propertyTitle = "Alias";
    this.settingsInputElementModel.newCompanyAlias.requiredPermission = PermissionService.Owner();
    this.settingsInputElementModel.newCompanyAlias.order = 2;

    // address
    this.settingsInputElementModel.address.propertyTitle = "Address";
    this.settingsInputElementModel.address.inputType = "address";
    this.settingsInputElementModel.address.order = 20;
    this.settingsInputElementModel.address.requiredPermission = PermissionService.Owner();

    // tax
    this.settingsInputElementModel.taxSeparator.propertyTitle = "Taxes";
    this.settingsInputElementModel.taxSeparator.inputType = "separator";
    this.settingsInputElementModel.taxSeparator.order = 30;
    this.settingsInputElementModel.taxSeparator.requiredPermission = PermissionService.Owner();

    this.settingsInputElementModel.taxNumber.propertyTitle = "Tax Number";
    this.settingsInputElementModel.taxNumber.requiredPermission = PermissionService.Owner();
    this.settingsInputElementModel.taxNumber.order = 31;

    this.settingsInputElementModel.taxId.propertyTitle = "Tax ID";
    this.settingsInputElementModel.taxId.requiredPermission = PermissionService.Owner();
    this.settingsInputElementModel.taxId.order = 32;

    this.deletePermission = PermissionService.CheckPermission(PermissionService.Owner());
  }

  deleteModel = () => {
    if (this.model === undefined || !this.deletePermission) return;
    this.companyHttpService.delete(this.model.id).subscribe(() => {
      this.globalAlertService.createAlertBannerModel("Unternehmen entfernt", "Unternehmen wurde erfolgreich entfernt", AlertLevel.success, 5000)
      this.globalAlertService.show();
    });
  };
  @Input() loadInputModel: Function = (id: string) => {
    this.companyHttpService.get(id).subscribe((model: CompanyDto) => {
      this.model = model;
      this.settingsInputElementModel.newCompanyName.originalPropertyValue = this.settingsInputElementModel.newCompanyName.changeableProperties.newPropertyValue = this.model?.name
      this.settingsInputElementModel.newCompanyAlias.originalPropertyValue = this.settingsInputElementModel.newCompanyAlias.changeableProperties.newPropertyValue = this.model?.alias

      this.settingsInputElementModel.taxNumber.originalPropertyValue = this.settingsInputElementModel.taxNumber.changeableProperties.newPropertyValue = this.model?.metadata?.taxNumber
      this.settingsInputElementModel.taxId.originalPropertyValue = this.settingsInputElementModel.taxId.changeableProperties.newPropertyValue = this.model?.metadata?.taxId


      if (!this.model.address) {
        this.model.address = new Address();
      }

      this.settingsInputElementModel.address.originalPropertyValue = JSON.parse(JSON.stringify(this.model?.address))
      this.settingsInputElementModel.address.changeableProperties.newPropertyValue = JSON.parse(JSON.stringify(this.model?.address))

      this.updateElements()
    });
  };

  @Input() saveChanges: Function = () => {

    if (this.model === undefined) return;

    this.requiresChanges.push(this.saveName())
    this.requiresChanges.push(this.saveAlias())
    this.requiresChanges.push(this.saveAddress())

    this.requiresChanges.push(this.saveVariable("TaxNumber", this.settingsInputElementModel.taxNumber))
    this.requiresChanges.push(this.saveVariable("TaxId", this.settingsInputElementModel.taxId))

    if (!this.changeRequestRequired()) return;

    this.executeQueueWrapper();

  };


  saveAlias(): HttpPropertyChangeStatus {
    if (this.model === undefined) return HttpPropertyChangeStatus.NotChanged;
    if (this.settingsInputElementModel.newCompanyAlias.changeableProperties.newPropertyValue === undefined) return HttpPropertyChangeStatus.NotChanged;
    if (this.settingsInputElementModel.newCompanyAlias.changeableProperties.newPropertyValue === this.settingsInputElementModel.newCompanyAlias.originalPropertyValue) return HttpPropertyChangeStatus.NotChanged;

    let observableQueueElement = new ObservableQueueElement();
    observableQueueElement.observable = this.companyHttpService.changeAlias(this.model.id, this.settingsInputElementModel.newCompanyAlias.changeableProperties.newPropertyValue);
    observableQueueElement.callback = (result: CompanyAliasResult) => {
      this.model = result.company;
      // The SERVER's alias, not what was typed: it lowercases the label (#637), so echoing the input back
      // would leave the field showing „FSL" while the company is reachable at `fsl`.
      this.settingsInputElementModel.newCompanyAlias.originalPropertyValue = this.settingsInputElementModel.newCompanyAlias.changeableProperties.newPropertyValue = this.model?.alias
      // Held, not posted: the shared success banner is raised AFTER every queued call has finished, so a
      // warning shown here would be overwritten by it. See `queueSettled`.
      // `?? null`, not a plain copy: the API serialises with NullValueHandling.Ignore, so on a clean save the
      // field is ABSENT from the wire, not null — and `undefined === null` is false, which would have turned
      // "no warning" into "never redirect" (caught by scripts/e2e/alias-dns.e2e.sh).
      this.dnsWarning = result.dnsWarning ?? null;
      observableQueueElement.successIndicator = true;
      this.updateElements()
    };
    observableQueueElement.callbackError = (error: any) => {
      // The alias rule is a named refusal since #637 (`E_COMPANY_ALIAS_INVALID_LABEL`), and it is the one the
      // operator can act on: the generic sentence stays as the fallback for everything else, which on this
      // endpoint is still a bare status.
      this.globalAlertService.createAndShow("Fehler",
        describeAdministrationError(error, "Fehler beim Speichern des Alias."), AlertLevel.error, 5000);
      observableQueueElement.successIndicator = false;
    };

    this.observableQueue.push(observableQueueElement);

    return HttpPropertyChangeStatus.Changed;
  }

  /**
   * Reports a DNS problem the alias save did **not** fail over.
   *
   * Raised after the shared „Erfolgreich" banner, and deliberately replacing it: the alias *was* stored, so
   * the success message is true — but it is no longer the whole truth, and the part the operator has to act
   * on is this one. Posted as a warning rather than an error for the same reason.
   *
   * @param allSucceeded whether the whole queue succeeded. A failed queue shows no success banner at all, and
   *                     its own error is the more important message — the warning waits for the next attempt.
   */
  /**
   * Stays on the screen when the save carried a DNS warning.
   *
   * The redirect is a full page reload, which destroys the global alert banner — so a warning raised a
   * moment later would be readable for two seconds and then vanish, on the one message here that asks the
   * operator to go and do something. A save with nothing to report redirects exactly as before.
   */
  protected override redirectAfterSave(): boolean {
    return this.dnsWarning === null;
  }

  protected override queueSettled(allSucceeded: boolean): void {
    const warning = this.dnsWarning;
    this.dnsWarning = null;
    if (!allSucceeded || warning === null) return;

    this.globalAlertService.createAndShow("Alias gespeichert, DNS-Eintrag offen",
      describeAdministrationCode(warning.code, DNS_WARNING_FALLBACK), AlertLevel.warning, 10000);
  }

  saveName(): HttpPropertyChangeStatus {
    if (this.model === undefined) return HttpPropertyChangeStatus.NotChanged;
    if (this.settingsInputElementModel.newCompanyName.changeableProperties.newPropertyValue === undefined) return HttpPropertyChangeStatus.NotChanged;
    if (this.settingsInputElementModel.newCompanyName.changeableProperties.newPropertyValue === this.settingsInputElementModel.newCompanyName.originalPropertyValue) return HttpPropertyChangeStatus.NotChanged;

    let observableQueueElement = new ObservableQueueElement();
    observableQueueElement.observable = this.companyHttpService.changeName(this.model.id, this.settingsInputElementModel.newCompanyName.changeableProperties.newPropertyValue);
    observableQueueElement.callback = (model: CompanyDto) => {
      this.model = model;
      this.settingsInputElementModel.newCompanyName.originalPropertyValue = this.settingsInputElementModel.newCompanyName.changeableProperties.newPropertyValue = this.model?.name
      observableQueueElement.successIndicator = true;
      this.updateElements()
    };
    observableQueueElement.callbackError = (error: any) => {
      this.globalAlertService.createAlertBannerModel("Fehler", "Fehler beim Speichern des Namens.", AlertLevel.error, 5000);
      this.globalAlertService.show();
      observableQueueElement.successIndicator = false;
    };

    this.observableQueue.push(observableQueueElement);

    return HttpPropertyChangeStatus.Changed;
  }

  saveAddress(): HttpPropertyChangeStatus {
    if (this.model === undefined) return HttpPropertyChangeStatus.NotChanged;
    if (this.settingsInputElementModel.address.changeableProperties.newPropertyValue === undefined) return HttpPropertyChangeStatus.NotChanged;
    if (JSON.stringify(this.settingsInputElementModel.address.changeableProperties.newPropertyValue) ===
      JSON.stringify(this.settingsInputElementModel.address.originalPropertyValue)) return HttpPropertyChangeStatus.NotChanged;

    let observableQueueElement = new ObservableQueueElement();
    observableQueueElement.observable = this.companyHttpService.setAddress(this.model.id, this.settingsInputElementModel.address.changeableProperties.newPropertyValue);
    observableQueueElement.callback = (model: CompanyDto) => {
      this.model = model;
      this.settingsInputElementModel.address.originalPropertyValue = JSON.parse(JSON.stringify(this.model?.address))
      this.settingsInputElementModel.address.changeableProperties.newPropertyValue = JSON.parse(JSON.stringify(this.model?.address))
      observableQueueElement.successIndicator = true;
      this.updateElements()
    };
    observableQueueElement.callbackError = (error: any) => {
      this.globalAlertService.createAlertBannerModel("Fehler", "Fehler beim Speichern der Adresse.", AlertLevel.error, 5000);
      this.globalAlertService.show();
      observableQueueElement.successIndicator = false;
    };

    this.observableQueue.push(observableQueueElement);

    return HttpPropertyChangeStatus.Changed;
  }

  saveVariable(variable: string, cModel: SettingsInputElementModel): HttpPropertyChangeStatus {
    if (this.model === undefined) return HttpPropertyChangeStatus.NotChanged;
    if (cModel.changeableProperties.newPropertyValue === undefined) return HttpPropertyChangeStatus.NotChanged;
    if (cModel.changeableProperties.newPropertyValue === cModel.originalPropertyValue) return HttpPropertyChangeStatus.NotChanged;

    let observableQueueElement = new ObservableQueueElement()
    observableQueueElement.observable = this.companyHttpService.setProperty(this.model.id, variable, cModel.changeableProperties.newPropertyValue);
    observableQueueElement.callback = (model: CompanyDto) => {
      this.model = model;
      cModel.originalPropertyValue = cModel.changeableProperties.newPropertyValue
      observableQueueElement.successIndicator = true;
      this.updateElements();
    }
    observableQueueElement.callbackError = (error: any) => {
      this.globalAlertService.createAndShow("Fehler", "Fehler beim Speichern des Werts.", AlertLevel.error);
      observableQueueElement.successIndicator = false;
    }

    this.observableQueue.push(observableQueueElement);

    return HttpPropertyChangeStatus.Changed;
  }
}


